import { ComponentRef, Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import {
  BehaviorSubject,
  concat,
  concatMap,
  delay,
  filter,
  from,
  Observable,
  of,
  Subscription,
  take, tap,
} from 'rxjs';
import { map } from 'rxjs/operators';
import { OverlayEventDetail } from '@ionic/core/dist/types/utils/overlays-interface';
import { FrontendModalSize } from './frontend-modal.interfaces';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

const PRESENT_DATA = Infinity;

@Injectable()
export class FrontendModalService {
  modalWithIdPresented$: Observable<string>;
  headerHeightSet$: Observable<number>;
  footerHeightSet$: Observable<number>;

  private readonly subscription = new Subscription();
  private readonly modalWithIdPresentedSource$ = new BehaviorSubject<string>(null);
  private readonly headerHeightSetSource$ = new BehaviorSubject<number>(null);
  private readonly footerHeightSetSource$ = new BehaviorSubject<number>(null);

  constructor(
    private readonly modalController: ModalController,
    private readonly breakpointObserver: BreakpointObserver,
  ) {
    this.modalWithIdPresented$ = this.modalWithIdPresentedSource$.asObservable();
    this.headerHeightSet$ = this.headerHeightSetSource$.asObservable();
    this.footerHeightSet$ = this.footerHeightSetSource$.asObservable();

    const orientationSub = this.breakpointObserver.observe([
      Breakpoints.HandsetPortrait,
      Breakpoints.HandsetLandscape,
      Breakpoints.TabletPortrait,
      Breakpoints.TabletLandscape,
      Breakpoints.WebPortrait,
      Breakpoints.WebLandscape,
    ])
      .pipe(
        tap(() => {
          this.modalWithIdPresentedSource$.next('true');
        }),
        delay(500),
        tap(() => {
          this.modalWithIdPresentedSource$.next('true');
        }),
      )
      .subscribe();
    this.subscription.add(orientationSub);
  }

  setHeader(height: number): void {
    this.headerHeightSetSource$.next(height);
  }

  setFooter(height: number): void {
    this.footerHeightSetSource$.next(height);
  }

  create<C, T>(
    // @ts-expect-error some kind of error here
    component: ComponentRef,
    componentProps: Partial<Record<keyof C, unknown>> = {},
    modalSize = FrontendModalSize.default,
    backdropDismiss: boolean = true,
    prePresentCallback?: (modal: HTMLIonModalElement) => void,
  ): Observable<T | undefined> {
    this.resetHeights();
    return from(this.modalController.create({
      backdropDismiss,
      component,
      componentProps,
      showBackdrop: true,
      animated: false,
      cssClass: `ion-modal-form ion-modal-backdrop ion-safe-areas-padding-horizontal ${modalSize}`,
    }))
      .pipe(
        concatMap((modal) => {
          if (prePresentCallback) {
            prePresentCallback(modal);
          }

          return concat(
            from(modal.present()).pipe(map(() => {
              this.modalWithIdPresentedSource$.next('hello');

              return PRESENT_DATA;
            })),
            from(modal.onWillDismiss<T>()),
          );
        }),
        filter(data => data !== PRESENT_DATA),
        map((res) => {
          this.resetHeights();
          const result = res as OverlayEventDetail<T>;

          return result.data;
        }),
      );
  }

  dismiss<T>(data?: T): void {
    from(this.modalController.getTop())
      .pipe(
        take(1),
        filter(res => res !== undefined),
        concatMap(() => from(this.modalController.dismiss(data))),
      )
      .subscribe();
  }

  dismissAsync<T>(data?: T): Observable<boolean> {
    return from(this.modalController.getTop())
      .pipe(
        take(1),
        concatMap((res) => {
          if (!res) {
            return of(false);
          }

          return from(this.modalController.dismiss(data));
        }),
      );
  }

  private resetHeights(): void {
    this.headerHeightSetSource$.next(null);
    this.footerHeightSetSource$.next(null);
    this.modalWithIdPresentedSource$.next(null);
  }
}
